import type { Locale, MethodologyDoc, SectorDefinition, Theme } from '../types';
import { chrome } from '../i18n';
import { PlatformBar } from '../components/PlatformBar';
import { Breadcrumb } from '../components/Breadcrumb';
import { icon } from '../components/Icon';

interface Props {
  sectors: SectorDefinition[];
  doc: MethodologyDoc;
  locale: Locale;
  theme: Theme;
  query?: string;
  hasCommitments: boolean;
}

interface FlatEntry {
  category: string;
  categoryIcon: string;
  term: string;
  definition: string;
  formula: string;
  meta: string;
  haystack: string;
}

/** Flattens methodology entries + every sector KPI into one searchable list. */
export function flattenMethodology(
  doc: MethodologyDoc,
  sectors: SectorDefinition[],
  locale: Locale,
): FlatEntry[] {
  const out: FlatEntry[] = [];
  for (const cat of doc.categories) {
    const catTitle = locale === 'ar' ? cat.title_ar : cat.title_en;
    for (const e of cat.entries) {
      const term = locale === 'ar' ? e.term_ar : e.term_en;
      const definition = locale === 'ar' ? e.def_ar : e.def_en;
      const meta = locale === 'ar' ? e.meta_ar : e.meta_en;
      out.push({
        category: catTitle,
        categoryIcon: cat.icon,
        term,
        definition,
        formula: e.formula,
        meta,
        haystack: `${term} ${definition} ${e.formula} ${meta}`.toLowerCase(),
      });
    }
  }
  // sector KPIs (from live data)
  const c = chrome(locale);
  const kpiCat = c.indicatorsBySector;
  for (const s of sectors) {
    const sname = s.content[locale].navLabel;
    for (const k of s.content[locale].kpis) {
      const dirLabel = k.direction === 'up' ? c.betterHigh : k.direction === 'down' ? c.betterLow : c.betterNeutral;
      const definition = `${k.desc ?? ''} (${sname} · ${c.unitLabel}: ${k.unit}${k.target ? ` · ${c.targetLabel}: ${k.target.label}` : ''})`;
      out.push({
        category: kpiCat,
        categoryIcon: 'bar-chart-3',
        term: k.label,
        definition,
        formula: `${dirLabel} · reported figure · status via Target Attainment`,
        meta: sname,
        haystack: `${k.label} ${k.desc ?? ''} ${sname} ${k.unit}`.toLowerCase(),
      });
    }
  }
  return out;
}

/** Renders the grouped results (called on first render and on every search). */
export function renderMethodologyResults(
  entries: FlatEntry[],
  locale: Locale,
  query: string,
): string {
  const c = chrome(locale);
  const q = query.trim().toLowerCase();
  const matched = q ? entries.filter((e) => e.haystack.includes(q)) : entries;
  if (matched.length === 0) {
    return `<div class="meth-empty">${icon('search')} ${c.noResults}</div>`;
  }
  // group by category, preserving order
  const groups: { name: string; icon: string; items: FlatEntry[] }[] = [];
  for (const e of matched) {
    let g = groups.find((x) => x.name === e.category);
    if (!g) {
      g = { name: e.category, icon: e.categoryIcon, items: [] };
      groups.push(g);
    }
    g.items.push(e);
  }
  const count = `<div class="meth-count">${matched.length} ${c.resultsCount}</div>`;
  const body = groups
    .map(
      (g) => `
    <section class="meth-group">
      <h3 class="meth-cat">${icon(g.icon)} <span>${g.name}</span></h3>
      <div class="meth-entries">
        ${g.items
          .map(
            (e) => `
          <article class="meth-entry">
            <div class="meth-term">${e.term}</div>
            <div class="meth-def">${e.definition}</div>
            <div class="meth-formula"><span class="meth-flabel">${c.formulaLabel}</span><code>${escapeHtml(e.formula)}</code></div>
            ${e.meta ? `<div class="meth-meta">${e.meta}</div>` : ''}
          </article>`,
          )
          .join('')}
      </div>
    </section>`,
    )
    .join('');
  return count + body;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function MethodologyView({ sectors, doc, locale, theme, query = '', hasCommitments }: Props): string {
  const c = chrome(locale);
  const entries = flattenMethodology(doc, sectors, locale);
  return `
    ${PlatformBar(sectors, 'methodology', locale, theme, hasCommitments)}
    ${Breadcrumb([
      { label: c.navNational, href: 'national', icon: 'gauge' },
      { label: c.navMethodology, icon: 'book-open' },
    ])}
    <section class="meth-head">
      <div>
        <h1>${icon('book-open')} ${c.methodologyTitle}</h1>
        <p>${c.methodologySubtitle}</p>
      </div>
    </section>
    <div class="meth-search">
      ${icon('search', 'icon meth-search-ic')}
      <input id="methSearch" type="search" autocomplete="off" placeholder="${c.searchPlaceholder}" aria-label="${c.searchPlaceholder}" value="${query.replace(/"/g, '&quot;')}" />
    </div>
    <div id="meth-results">${renderMethodologyResults(entries, locale, query)}</div>
    <div id="modal-root"></div>`;
}
